import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { paymentEvidenceApi } from "../../services/paymentEvidenceApi";
import { payrollApi } from "../../services/payrollApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function PeriodPaymentsPage() {
  const { periodId } = useParams();
  const { t } = useLocalization();
  const { canRecordPaymentEvidence, canConfirmPaymentEvidence, canViewPaymentEvidence } = usePermissions();
  const [period, setPeriod] = useState(null);
  const [rows, setRows] = useState([]);
  const [recordingFor, setRecordingFor] = useState(null);
  const [form, setForm] = useState({ payment_method: "BANK_TRANSFER", payment_date: "", payment_amount: "", transfer_reference: "", notes: "" });
  const [file, setFile] = useState(null);

  const load = useCallback(() => {
    payrollApi.getPeriod(periodId).then((r) => setPeriod(r.data)).catch(() => {});
    paymentEvidenceApi.listPeriodPayments(periodId).then((r) => setRows(r.data)).catch(() => {});
  }, [periodId]);

  useEffect(() => { if (canViewPaymentEvidence) load(); }, [canViewPaymentEvidence, load]);

  if (!canViewPaymentEvidence) return <p className="text-sm text-gray-500" data-testid="payments-access-denied">{t("common.accessDenied")}</p>;

  const openRecordForm = (employeeId, expectedAmount) => {
    setRecordingFor(employeeId);
    setForm({ payment_method: "BANK_TRANSFER", payment_date: "", payment_amount: expectedAmount, transfer_reference: "", notes: "" });
    setFile(null);
  };

  const submitRecord = async (employeeId) => {
    if (!form.payment_date || !form.payment_amount) { toast.error(t("mgmtPayroll.paymentDateAmountRequired")); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
    if (file) fd.append("evidence_file", file);
    try {
      await paymentEvidenceApi.recordPayment(periodId, employeeId, fd);
      toast.success(t("mgmtPayroll.paymentEvidenceRecorded"));
      setRecordingFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const confirm = async (evidenceId) => {
    try { await paymentEvidenceApi.confirmPayment(evidenceId); toast.success(t("mgmtPayroll.paymentConfirmed")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="period-payments-page">
      <PageHelpBanner pageKey="period-payments" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtPayroll.periodPaymentsHelp")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtPayroll.paymentEvidence")} - {period?.payroll_month}</h2>
        <p className="text-sm text-gray-500">{t("mgmtPayroll.periodPaymentsSubtitle")}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colExpected")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colPaid")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colDiff")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.employee_id} className="border-t border-gray-100" data-testid={`payment-row-${r.employee_id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{r.employee_name || r.employee_id}</td>
                <td className="px-4 py-2 text-gray-600">¥{r.expected_amount}</td>
                <td className="px-4 py-2 text-gray-600">{r.payment_amount != null ? `¥${r.payment_amount}` : "-"}</td>
                <td className={`px-4 py-2 font-semibold ${r.difference && Number(r.difference) !== 0 ? "text-red-600" : "text-gray-500"}`}>{r.difference ?? "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2 space-x-2">
                  {canRecordPaymentEvidence && ["READY_FOR_PAYMENT", "RECONCILIATION_REQUIRED", "PAYMENT_FAILED"].includes(r.status) && (
                    <Button size="sm" variant="link" className="h-auto p-0 text-xs" data-testid={`record-payment-btn-${r.employee_id}`} onClick={() => openRecordForm(r.employee_id, r.expected_amount)}>{t("mgmtPayroll.recordPayment")}</Button>
                  )}
                  {canConfirmPaymentEvidence && ["PAYMENT_INITIATED", "PAYMENT_EVIDENCE_PENDING"].includes(r.status) && r.id && (
                    <Button size="sm" variant="link" className="h-auto p-0 text-xs text-emerald-600" data-testid={`confirm-payment-btn-${r.employee_id}`} onClick={() => confirm(r.id)}>{t("common.confirm")}</Button>
                  )}
                  {r.id && r.evidence_storage_path && (
                    <a data-testid={`view-evidence-link-${r.employee_id}`} href={paymentEvidenceApi.getEvidenceFileUrl(r.id)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-gray-500 hover:underline">{t("mgmtPayroll.evidence")}</a>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-payment-rows">{t("mgmtPayroll.noCalculatedEmployees")}</td></tr>}
          </tbody>
        </table>
      </div>

      {recordingFor && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3 max-w-lg" data-testid="record-payment-panel">
          <p className="text-sm font-semibold text-gray-800">{t("mgmtPayroll.recordPaymentFor")} {recordingFor}</p>
          <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
            <SelectTrigger data-testid="payment-method-select" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="BANK_TRANSFER">{t("mgmtPayroll.bankTransfer")}</SelectItem>
              <SelectItem value="CASH">{t("mgmtPayroll.cash")}</SelectItem>
              <SelectItem value="OTHER">{t("mgmtPayroll.other")}</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="payment-date-input" type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
          <Input data-testid="payment-amount-input" value={form.payment_amount} onChange={(e) => setForm({ ...form, payment_amount: e.target.value })} placeholder={t("mgmtPayroll.amountPaid")} />
          <Input data-testid="transfer-reference-input" value={form.transfer_reference} onChange={(e) => setForm({ ...form, transfer_reference: e.target.value })} placeholder={t("mgmtPayroll.transferReference")} />
          <Input data-testid="payment-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t("mgmtPayroll.notesOptional")} />
          <input data-testid="evidence-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" onChange={(e) => setFile(e.target.files[0])} className="text-sm w-full" />
          <div className="flex gap-2">
            <Button data-testid="submit-payment-btn" onClick={() => submitRecord(recordingFor)}>{t("common.save")}</Button>
            <Button variant="outline" data-testid="cancel-payment-btn" onClick={() => setRecordingFor(null)}>{t("common.cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
